import { TemplateData, TemplateTypes } from './models/template-types-model';
import { Injectable } from '@angular/core';
import { Activity } from '@cbuild/common';
import { SelectOption, SelectOptions } from '@cbuild/controls';
import { CsHttpClientService } from '@cbuild/cs-common-data-services';
import { UserTypes } from '@cbuild/shared';
import { toQueryString } from '@cbuild/utils';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs';
import { StageChangeResponseModel } from '../../../sales/common/lead-functions/models/stage-change-response.model';
import { OrganizationConfiguration } from '../../../sales/leads/lead-activity/create-customer/models/organization-configuration.model';
import {
  FeedbackModel,
  SubFeedbackModel
} from '../../../sales/leads/models/feedback.model';
import { CallFeedbackRequestModel, StageTemplate } from './models/call-feedback-request-model';
import {
  CallResponseModel,
  SelectOptionsWithStages
} from './models/call-response-model';


export type SelectOptionsWithPath = Array<
  SelectOption & {
    templatePath: boolean;
  }
>;


@Injectable({ providedIn: 'root' })
export class CallCenterService {
  constructor(private http: CsHttpClientService) {}

  public pauseOrResumeCall(id: number): Observable<CallResponseModel> {
    return this.http.writeText<CallResponseModel>(
      'api/call-center/queue/pause-resume',
      {
        queueId: id
      }
    );
  }

  public clickToCall(
    leadId: number,
    isAlternateMobileCall: boolean
  ): Observable<{ message: string }> {
    return this.http.read<{ message: string }>(
      `api/call-center/initiate/call${toQueryString({
        leadId,
        isAlternateMobileCall
      })}`
    );
  }

  public triggerNextCall(): Observable<{}> {
    return this.http.writeText<{}>('api/call-center/queue/next-call', {});
  }

  public KillQueue(id: number): Observable<CallResponseModel> {
    return this.http.writeText<CallResponseModel>(
      'api/call-center/queue/kill',
      {
        queueId: id
      }
    );
  }

  public getCallUpdate(leadId: number | null): Observable<CallResponseModel> {
    return this.http
      .read<Array<CallResponseModel>>(
        `api/lead/lead-call/feedback/get${toQueryString({ leadId })}`
      )
      .pipe(map(res => res[0]));
  }

  public getLeadStages(pipelineId: number | null): Observable<SelectOptions> {
    return this.http
      .read<SelectOptions>(
        `api/common/lead/stages${toQueryString({ pipelineId })}`
      )
      .pipe(map(resp => (resp ? resp : [])));
  }

  public getSubSources(sourceId: number): Observable<SelectOptions> {
    return this.http
      .read<SelectOptions>(
        `api/common/subSources/get${toQueryString({
          sourceId
        })}`
      )
      .pipe(map(resp => (resp ? resp : [])));
  }

  public getUsers(): Observable<SelectOptions> {
    return this.http.read<SelectOptions>(
      `api/common/users/get?type=${UserTypes.USER}`
    );
  }

  public getCallConferenceEnabled(
    type: number
  ): Observable<{
    isCallConferenceEnabled: boolean;
  }> {
    if (type) {
      return this.http.read<{ isCallConferenceEnabled: boolean }>(
        `api/call-center/is-call-conference-enabled?type=${type}`
      );
    }
    return of({ isCallConferenceEnabled: false });
  }

  public callConference(data: {
    leadActionLogId: number;
    userId: string;
  }): Observable<{}> {
    return this.http.writeText<{}>('api/call-center/call-conference', data);
  }

  public getProperties(companyId: number | null): Observable<SelectOptions> {
    return this.http
      .read<SelectOptions>(
        `api/common/properties/get${toQueryString({ companyId })}`
      )
      .pipe(map(resp => (resp ? resp : [])));
  }

  public getPipelines(): Observable<SelectOptions> {
    return this.http.read<SelectOptions>('api/common/pipelines/get');
  }

  public getAllPipelines(): Observable<SelectOptions> {
    return this.http
      .read<SelectOptions>('api/common/all/pipelines/get')
      .pipe(map(resp => (resp ? resp : [])));
  }

  public getLeadFeedbacksByStageId(
    pipelineId: number | null,
    stageId: number
  ): Observable<SelectOptions> {
    return this.http
      .read<SelectOptions>(
        `api/lead/feedbacks/get${toQueryString({
          pipelineId,
          stageId
        })}`
      )
      .pipe(map(resp => (resp ? resp : [])));
  }

  public getSiteVisitUsers(
    pipelineId: number | null
  ): Observable<SelectOptions> {
    return this.http
      .read<SelectOptions>(
        `api/sitevisit/pipeline/users/get${toQueryString({
          pipelineId
        })}`
      )
      .pipe(map(resp => (resp ? resp : [])));
  }

  public getTaskUsers(pipelineId: number | null): Observable<SelectOptions> {
    return this.http
      .read<SelectOptions>(
        `api/common/task/pipeline/users/get${toQueryString({
          pipelineId
        })}`
      )
      .pipe(map(resp => (resp ? resp : [])));
  }

  public saveCallFeedback(value: CallFeedbackRequestModel): Observable<{}> {
    return this.http.writeText<{}>(
      'api/lead/lead-call/feedback/save',
      this.prepareLeadData(value)
    );
  }

  public updateFeedback(
    value: CallFeedbackRequestModel
  ): Observable<StageChangeResponseModel> {
    return this.http
      .writeText<Array<StageChangeResponseModel>>(
        'api/lead/lead/feedback/update',
        this.prepareLeadData(value)
      )
      .pipe(map(res => res[0]));
  }

  public saveCallActionLog(data: {
    leadId: number;
  }): Observable<{ activityGroupId: string }> {
    return this.http.writeText<{ activityGroupId: string }>(
      'api/lead/lead-call/feedback/action-log/update',
      data
    );
  }

  private prepareLeadData(
    value: CallFeedbackRequestModel
  ): CallFeedbackRequestModel {
    let result: CallFeedbackRequestModel = {
      leadId: value.leadId,
      pipelineId: value.pipelineId,
      stageId: value.stageId,
      currentStageId: value.currentStageId,
      stageTemplateId: value.stageTemplateId,
      feedbackId: value.feedbackId == 0 ? null : value.feedbackId,
      leadActionLogId: value.leadActionLogId,
      remarks: value.remarks,
      subSourceId: value.subSourceId,
      name: value.name,
      email: value.email,
      alternateEmail: value.alternateEmail,
      alternateMobile: value.alternateMobile,
      callRecords: value.callRecords,
      propertyId: value.propertyId,
      subFeedbackId: value.subFeedbackId,
      isEmail: value.isEmail,
      isSms: value.isSms,
      isWhatsapp: value.isWhatsapp,
      emailCommunication: value.emailCommunication,
      smsCommunication: value.smsCommunication,
      whatsappCommunication: value.whatsappCommunication
    };
    result = this.addIfNote(value, result);
    result = this.addIfTask(value, result);
    result = this.addIfSitevisit(value, result);
    return result;
  }

  private addIfNote(
    value: CallFeedbackRequestModel,
    result: CallFeedbackRequestModel
  ): CallFeedbackRequestModel {
    const note = value.note;
    if (note?.note) {
      result.note = {
        note: note.note
      };
    }
    return result;
  }

  private addIfTask(
    value: CallFeedbackRequestModel,
    result: CallFeedbackRequestModel
  ): CallFeedbackRequestModel {
    const task = value.task;
    if (task?.task) {
      result.task = {
        assignedTo: task.assignedTo,
        description: task.description,
        dueDate: task.dueDate,
        remindOn: task.remindOn,
        task: task.task,
        type: task.type,
        triggerType: task.triggerType
      };
    }
    return result;
  }

  private addIfSitevisit(
    value: CallFeedbackRequestModel,
    result: CallFeedbackRequestModel
  ): CallFeedbackRequestModel {
    const siteVisit = value.siteVisit;
    if (siteVisit?.startDate) {
      result.siteVisit = {
        remindOn: siteVisit.remindOn,
        startDate: siteVisit.startDate,
        description: siteVisit.description,
        siteVisitedDate: siteVisit.siteVisitedDate,
        status: siteVisit.status,
        assignTo: siteVisit.assignTo
      };
    }
    return result;
  }

  public getLeadActivities(data: {
    leadId: number;
  }): Observable<Array<Activity>> {
    return this.http.writeText<Array<Activity>>('api/lead/activity/get', data);
  }

  public getPriceConfiguration(
    id: number
  ): Observable<OrganizationConfiguration> {
    return this.http
      .read<Array<OrganizationConfiguration>>(
        `api/settings/general-settings/get${toQueryString({
          id
        })}`
      )
      .pipe(map(_ => _[0]));
  }

  public getStageTemplate(
    stageId: number
  ): Observable<StageTemplate> {
    return this.http
      .read<Array<StageTemplate>>(
        `api/lead/stage/template/get${toQueryString({
          stageId
        })}`
      )
      .pipe(map(_ => _[0]));
  }

  public getLeadFeedbacks(
    pipelineId: number | null,
    stageId: number | null
  ): Observable<Array<FeedbackModel>> {
    return this.http.read<Array<FeedbackModel>>(
      `api/lead/feedbacks/get${toQueryString({
        pipelineId,
        stageId
      })}`
    );
  }

  public getLeadSubFeedbacks(
    feedbackId: number | null
  ): Observable<Array<SubFeedbackModel>> {
    return this.http.read<Array<SubFeedbackModel>>(
      `api/common/subFeedbacks/get${toQueryString({
        feedbackId
      })}`
    );
  }

  public getHasMandatoryFeedback(
    stageId: number
  ): Observable<{ hasMandatory: boolean }> {
    return this.http.read<{ hasMandatory: boolean }>(
      `api/settings/general-settings/get${toQueryString({
        stageId
      })}`
    );
  }

  public getPropertyTeamUserCount(
    propertyId: number
  ): Observable<{ count: number }> {
    return this.http.read<{ count: number }>(
      `api/lead/property/team-users/count/get${toQueryString({
        propertyId
      })}`
    );
  }

  public getLeadStagesWithMandatory(
    pipelineId: number | null
  ): Observable<SelectOptionsWithStages> {
    return this.http
      .read<SelectOptionsWithStages>(
        `api/common/lead/stages${toQueryString({ pipelineId })}`
      )
      .pipe(map(resp => (resp ? resp : [])));
  }

  public getEmailTemplates(
    moduleId: number
  ): Observable<Array<TemplateTypes>> {
    return this.http.read<Array<TemplateTypes>>(
      `api/common/email/templates/get${toQueryString({
        moduleId
      })}`
    );
  }

  public getEmailTemplateData(
    propertyId: number,
    templateId: number
  ): Observable<TemplateData> {
    return this.http.read<Array<TemplateData>>(
      `api/common/email/template-details/get${toQueryString({
        propertyId, templateId
      })}`
    ).pipe(map(_ => _[0]));
  }

  public getSmsTemplates(
    moduleId: number
  ): Observable<Array<TemplateTypes>> {
    return this.http.read<Array<TemplateTypes>>(
      `api/common/sms/templates/get${toQueryString({
        moduleId
      })}`
    );
  }

  public getSmsTemplateData(
    propertyId: number,
    templateId: number,
    isOtherTemplate: boolean
  ): Observable<TemplateData> {
    return this.http.read<Array<TemplateData>>(
      `api/common/sms/template-details/get${toQueryString({
        propertyId, templateId, isOtherTemplate
      })}`
    ).pipe(map(_ => _[0]));
  }

  public getWhatsappTemplates(
    moduleId: number
  ): Observable<Array<TemplateTypes>> {
    return this.http.read<Array<TemplateTypes>>(
      `api/common/whatsapp/templates/get${toQueryString({
        moduleId
      })}`
    );
  }

  public getWhatsappTemplateData(
    propertyId: number,
    templateId: number,
    isOtherTemplate: boolean
  ): Observable<TemplateData> {
    return this.http.read<Array<TemplateData>>(
      `api/common/whatsApp/template-details/get${toQueryString({
        propertyId, templateId, isOtherTemplate
      })}`
    ).pipe(map(_ => _[0]));
  }
}
